'use client'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import Portal from './Portal'
import { easeShort } from '@/lib/transitions'
import { cn } from '@/lib/utils'

type SheetProps = React.HTMLAttributes<HTMLElement> & {
  trigger: React.ReactNode
  isVisible: boolean
  isMenu?: boolean
  close: () => void
  fullHeight?: boolean
  indicatorClass?: string
  disableScroll?: boolean
  className?: string
}

export default function Sheet({
  trigger,
  isVisible,
  close,
  children,
  isMenu,
  fullHeight = false,
  indicatorClass,
  disableScroll: _disableScroll = true,
  className,
}: SheetProps) {
  const [shouldRender, setShouldRender] = useState<boolean>(false)
  const [mounted, setMounted] = useState(false)
  const [contentHeight, setContentHeight] = useState<number>(0)
  const [isAtTop, setIsAtTop] = useState(true)
  const contentRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleDragEnd = (_: any, info: { offset: { y: number }; velocity: { y: number } }) => {
    const shouldClose = info.offset.y > 50 || info.velocity.y > 500
    if (shouldClose) {
      close()
    }
  }

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setIsAtTop(scrollContainerRef.current.scrollTop === 0)
    }
  }

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    setShouldRender(isVisible)
  }, [isVisible])

  useEffect(() => {
    if (!contentRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.contentRect.height + 21)
      }
    })

    observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [shouldRender])
  const sheetContent = (
    <AnimatePresence>
      {shouldRender && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { delay: 0.2 } }}
          transition={easeShort}
          className="fixed left-0 top-0 z-[9999] flex h-dvh w-full items-end overflow-hidden pt-20"
        >
          <motion.div
            ref={sheetRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={easeShort}
            drag={isAtTop ? 'y' : false}
            dragListener={isAtTop}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.2 }}
            dragSnapToOrigin
            dragDirectionLock
            dragPropagation={!isAtTop}
            onDragEnd={handleDragEnd}
            className={cn(
              'relative z-50 flex max-h-[calc(100dvh-79px)] w-full flex-col overflow-hidden rounded-t-xl bg-white transition-[height] duration-300 dark:border-x dark:border-t dark:border-white/10 dark:bg-black',
              className,
            )}
            style={{
              height: fullHeight ? '100%' : contentHeight || 'auto',
            }}
          >
            <div
              className={cn(
                'flex h-5 w-full shrink-0 justify-center',
                indicatorClass ??
                  'bg-white dark:bg-black [&>span]:bg-black/10 dark:[&>span]:bg-white/20',
              )}
            >
              <span className="mt-1.5 h-1 w-10 rounded-full"></span>
            </div>

            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto"
              style={{ touchAction: 'pan-y' }}
            >
              <div ref={contentRef}>{children}</div>
            </div>
          </motion.div>

          <div className="absolute inset-0 z-0 bg-black/40" onClick={close}></div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return (
    <>
      {trigger}
      {mounted && isMenu ? <Portal>{sheetContent}</Portal> : sheetContent}
    </>
  )
}
